import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DownloadIcon } from '../../../../../icons/SvgIcons';
import { PrimaryButton } from '../../../../ui/Button/Buttons';

type ExportFormat = 'csv' | 'json' | 'pdf';

type ExportOption<T extends string> = { value: T; label: string };

const DEFAULT_EXPORT_OPTIONS: ExportOption<ExportFormat>[] = [
  { value: 'csv', label: 'CSV' },
  { value: 'json', label: 'JSON' },
  { value: 'pdf', label: 'PDF' },
];

interface ExportDropdownProps<T extends string = ExportFormat> {
  onExport: (format: T) => void;
  // Defaults to CSV/JSON/PDF.
  options?: ExportOption<T>[];
  label?: string;
  // Header shown inside the dropdown, above the option list - the button
  // itself just says "Export"/"Download Log", which doesn't say what CSV/
  // JSON/PDF actually are once the menu is open.
  menuLabel?: string;
  // A visually separate group below the main options, with its own header -
  // for export-like actions that aren't a format of the same export (e.g. a
  // tamper-evidence report alongside the usual log exports), so they don't
  // read as a fourth sibling of CSV/JSON/PDF.
  secondarySection?: {
    label: string;
    options: ExportOption<T>[];
  };
}

export function ExportDropdown<T extends string = ExportFormat>({
  onExport,
  options,
  label = 'Download Log',
  menuLabel = 'Export as',
  secondarySection,
}: ExportDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0
  });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // close dropdown on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsOpen(false);

      const button = buttonRef.current?.querySelector('button');
      button?.blur();

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (format: T) => {
    onExport(format);
    setIsOpen(false);
  };

  const exportOptions = options ?? (DEFAULT_EXPORT_OPTIONS as unknown as ExportOption<T>[]);

  return (
    <div ref={buttonRef} className="relative">
      <PrimaryButton onClick={handleToggle} icon={<DownloadIcon />} size="xs">
        {label}
      </PrimaryButton>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: '180px',
              zIndex: 9999
            }}
            className="mt-1 bg-white rounded-md shadow-lg"
          >
            <div className="px-4 py-2 text-xs font-medium uppercase text-gray-550 border-b border-gray-100">
              {menuLabel}
            </div>
            {exportOptions.map((option) => (
              <div
                key={option.value}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionClick(option.value);
                }}
                className="px-4 py-2 cursor-pointer font-inter font-normal text-sm leading-5 text-[#4E4E56] hover:bg-[#F7F7F8] flex items-center"
                style={{ gap: '8px' }}
              >
                {option.label}
              </div>
            ))}
            {secondarySection && secondarySection.options.length > 0 && (
              <>
                <div className="px-4 py-2 text-xs font-medium uppercase text-gray-550 border-t border-b border-gray-100">
                  {secondarySection.label}
                </div>
                {secondarySection.options.map((option) => (
                  <div
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(option.value);
                    }}
                    className="px-4 py-2 cursor-pointer font-inter font-normal text-sm leading-5 text-[#4E4E56] hover:bg-[#F7F7F8] flex items-center"
                    style={{ gap: '8px' }}
                  >
                    {option.label}
                  </div>
                ))}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
